import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ListHeader from "../../molecules/ListHeader/ListHeader";
import ButtonLoader from "../../atoms/buttonLoader";
import { createOrganizationManager } from "../../services/service";
import { CustomToast } from "../../atoms/toastMessage";
import { useNavigate } from "react-router-dom";
import Phone from "../../atoms/phone";
import { DatePicker } from "antd";
import dayjs from "dayjs";

const normalizeGenderForApi = (value) => {
  if (value === "prefer_not_to_say") return "other";
  return value;
};

const AddOrganization = () => {
  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "non-binary", label: "Non-binary" },
    { value: "two-spirit", label: "Two-Spirit" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = React.useRef(null);
  const navigate = useNavigate();

  const plans = [
    { id: "price_1TGcrxA1DZhFpfbJ9gkHITBm", plan_name: "Basic - Monthly" },
    { id: "price_1TGcrtA1DZhFpfbJRAw0m1W8", plan_name: "Basic - Yearly" },
    { id: "price_1TGcrnA1DZhFpfbJk7DUzJbg", plan_name: "Premium - Monthly" },
    { id: "price_1TGcroA1DZhFpfbJ2U3ryGXo", plan_name: "Premium - Yearly" },
    { id: "price_1TGcrnA1DZhFpfbJ01zwwMlU", plan_name: "Ultimate - Monthly" },
    { id: "price_1TGcrnA1DZhFpfbJlcjRF0Bm", plan_name: "Ultimate - Yearly" },
    { id: "price_1THlgvA1DZhFpfbJFAzeGQmy", plan_name: "Trial Plan" },
  ];

  const formik = useFormik({
    initialValues: {
      org_title: "",
      plan_id: "",
      subscription_end_date: "",
      username: "",
      email: "",
      phoneNo: "",
      image: null,
      gender: "",
      experience: "",
      expertise: "",
      bio: "",
    },
    validationSchema: Yup.object({
      org_title: Yup.string()
        .required("Organization title is required")
        .min(2, "Organization title must be at least 2 characters"),
      plan_id: Yup.string().required("Plan is required"),
      subscription_end_date: Yup.string().required("Subscription end date is required"),
      username: Yup.string()
        .required("Manager name is required")
        .min(2, "Name must be at least 2 characters"),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      phoneNo: Yup.string().required("Phone number is required"),
      gender: Yup.string().required("Gender is required"),
      experience: Yup.string()
        .matches(/^\d+$/, "Experience must be a number")
        .required("Experience is required"),
      expertise: Yup.string().required("Expertise is required"),
      bio: Yup.string()
        .required("Bio is required")
        .min(10, "Bio must be at least 10 characters"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      setIsLoading(true);
      try {
        const formData = new FormData();
        formData.append("org_title", values.org_title);
        formData.append("plan_id", values.plan_id);
        formData.append("subscription_end_date", values.subscription_end_date);
        formData.append("username", values.username);
        formData.append("email", values.email);
        formData.append("phoneNo", values.phoneNo);
        formData.append("gender", normalizeGenderForApi(values.gender));
        formData.append("experience", values.experience);
        formData.append("expertise", values.expertise);
        formData.append("bio", values.bio);

        if (values.image) {
          formData.append("image", values.image);
        }

        const response = await createOrganizationManager(formData);

        if (response?.success) {
          CustomToast({
            type: "success",
            message: "Organization and Manager created successfully",
          });
          setIsLoading(false);
          setSubmitting(false);
          navigate("/super-admin/subscription-management");
        } else {
          CustomToast({
            type: "error",
            message: response?.message || "Failed to create organization",
          });
          setIsLoading(false);
          setSubmitting(false);
        }
      } catch (error) {
        console.error("Error creating organization:", error);
        CustomToast({
          type: "error",
          message: error?.response?.data?.message || "Failed to create organization",
        });
        setIsLoading(false);
        setSubmitting(false);
      }
    },
  });

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      formik.setFieldValue("image", file);
    }
  };

  const handleRemoveFile = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    formik.setFieldValue("image", null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="w-full">
      <div className="col-12">
        <ListHeader
          mainHeading="Add Organization"
          linkBreadCrum="/super-admin/subscription-management"
          blinkBreadCrumText="Subscription Management"
          blinkBreadCrumText1="Add Organization"
          searchShow={false}
          btnShow={false}
        />
      </div>

      <div className="w-full bg-white rounded-lg shadow-sm p-6 mt-4">
        <form onSubmit={formik.handleSubmit}>
          {/* Organization Information Section */}
          <div className="mb-6">
            <h3 className="text-[18px] font-semibold text-[#1E3A8A] mb-4 border-b pb-2">
              Organization Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Organization Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="org_title"
                  placeholder="Enter organization title"
                  className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.org_title && formik.errors.org_title
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.org_title}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.org_title && formik.errors.org_title && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.org_title}</p>
                )}
              </div>

              {/* Plan Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subscription Plan <span className="text-red-500">*</span>
                </label>
                <select
                  name="plan_id"
                  className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.plan_id && formik.errors.plan_id
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.plan_id}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select Plan</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.plan_name}
                    </option>
                  ))}
                </select>
                {formik.touched.plan_id && formik.errors.plan_id && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.plan_id}</p>
                )}
              </div>

              {/* Subscription End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subscription End Date <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  placeholder="Select end date"
                  className={`w-full h-[52px] border rounded ${formik.touched.subscription_end_date && formik.errors.subscription_end_date
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.subscription_end_date ? dayjs(formik.values.subscription_end_date) : null}
                  onChange={(date, dateString) => {
                    formik.setFieldValue("subscription_end_date", dateString);
                  }}
                  onBlur={() => formik.setFieldTouched("subscription_end_date", true)}
                  format="YYYY-MM-DD"
                />
                {formik.touched.subscription_end_date && formik.errors.subscription_end_date && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.subscription_end_date}</p>
                )}
              </div>
            </div>
          </div>

          {/* Manager Information Section */}
          <div className="mb-6">
            <h3 className="text-[18px] font-semibold text-[#1E3A8A] mb-4 border-b pb-2">
              Manager Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Manager Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="username"
                  placeholder="Enter manager name"
                  className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.username && formik.errors.username
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.username && formik.errors.username && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.username}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.email && formik.errors.email
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.email}</p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <Phone
                  name="phoneNo"
                  value={formik.values.phoneNo}
                  handleChange={(value, name) => formik.setFieldValue(name, value)}
                  field={{
                    onChange: formik.handleChange,
                    onBlur: formik.handleBlur,
                    name: "phoneNo",
                  }}
                />
                {formik.touched.phoneNo && formik.errors.phoneNo && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.phoneNo}</p>
                )}
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender <span className="text-red-500">*</span>
                </label>
                <select
                  name="gender"
                  className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.gender && formik.errors.gender
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.gender}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value="">Select Gender</option>
                  {genderOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {formik.touched.gender && formik.errors.gender && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.gender}</p>
                )}
              </div>

              {/* Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Experience <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="experience"
                  placeholder="e.g., 5"
                  className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.experience && formik.errors.experience
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.experience}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/\D/g, "");
                    formik.setFieldValue("experience", numericValue);
                  }}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.experience && formik.errors.experience && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.experience}</p>
                )}
              </div>

              {/* Expertise */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expertise <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="expertise"
                  placeholder="e.g., Construction Management"
                  className={`w-full h-[52px] px-3 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.expertise && formik.errors.expertise
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.expertise}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.expertise && formik.errors.expertise && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.expertise}</p>
                )}
              </div>

              {/* Bio */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager Bio <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="bio"
                  placeholder="Enter manager bio"
                  rows="4"
                  className={`w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A] ${formik.touched.bio && formik.errors.bio
                    ? "border-red-500"
                    : "border-gray-300"
                    }`}
                  value={formik.values.bio}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.bio && formik.errors.bio && (
                  <p className="text-red-500 text-sm mt-1">{formik.errors.bio}</p>
                )}
              </div>

              {/* Profile Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Manager Profile Image
                </label>
                {formik.values.image ? (
                  <div className="flex items-center w-full h-[52px] p-2 border border-gray-300 rounded gap-4 overflow-hidden">
                    <p className="text-gray-700 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                      {formik.values.image?.name || ""}
                    </p>
                    <button
                      onClick={handleRemoveFile}
                      type="button"
                      className="shrink-0 text-red-500 hover:text-red-700 text-xl"
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full max-w-full h-[52px] px-2 pt-[11px] border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]"
                  />
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Accepted formats: JPG, PNG, GIF. Max size: 5MB
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => navigate("/super-admin/subscription-management")}
              className="px-6 h-[44px] border border-gray-300 text-gray-700 font-medium rounded-[4px] hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              onClick={(e) => {
                if (Object.keys(formik.errors).length > 0) {
                  Object.keys(formik.values).forEach(key => {
                    formik.setFieldTouched(key, true);
                  });
                }
              }}
              className="px-6 h-[44px] bg-[#F97316] text-white font-medium rounded-[4px] hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
            >
              {isLoading ? <ButtonLoader /> : "Add Organization"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddOrganization;
